import React, { useState, useEffect } from 'react';
import {
  crawlMatches,
  crawlMatchesPartial,
  crawlByDate,
  getInitialData,
} from '../api';
import { toast } from 'react-toastify';
import PromptManager from './PromptManager';
import SchedulerManager from './SchedulerManager';
import CrawlerHistory from './CrawlerHistory';
import CrawlerRequest from './CrawlerRequest';
import PredictionManager from './PredictionManager';
import PsynetManager from './PsynetManager';
import { commonStyles, spacing } from '../styles/design-system';
import { User } from '../api/auth';
import { useAdminList } from '../hooks/useAdminList';

interface ServerConfigProps {
  currentUser: User;
}

const ServerConfig: React.FC<ServerConfigProps> = ({ currentUser }) => {
  const isAdministrator = currentUser.role === 'administrator';
  const [activeTab, setActiveTab] = useState<'prompts' | 'scheduler' | 'crawler' | 'prediction' | 'psynet'>('prompts');

  // Crawler states
  const [crawlerLoading, setCrawlerLoading] = useState(false);
  const [crawlerAiProvider, setCrawlerAiProvider] = useState('');
  const [initialData, setInitialData] = useState<any>(null);

  // 관리자 목록 (administrator만 자동 조회)
  const { adminList } = useAdminList({ immediate: isAdministrator });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const data = await getInitialData();
      setInitialData(data);
    } catch (error) {
      console.error('Failed to load initial data:', error);
    }
  };

  const handleCrawlerQuery = async (sport: string, date: string, partial: boolean, byDate?: boolean, endDate?: string, adminId?: number, notionAdminId?: number) => {
    setCrawlerLoading(true);
    try {
      if (byDate) {
        const data = await crawlByDate(date, crawlerAiProvider || undefined, endDate, adminId, notionAdminId);
        if (endDate) {
          toast.success(`${date}~${endDate} 기간의 모든 종목 크롤링이 완료되었음. (총 ${data.count}개 경기, ${data.date_count || 1}일)`);
        } else {
          toast.success(`${date} 날짜의 모든 종목 크롤링이 완료되었음. (총 ${data.count}개 경기)`);
        }
      } else if (partial) {
        await crawlMatchesPartial(sport, undefined, date || undefined, crawlerAiProvider || undefined, adminId, notionAdminId);
        toast.success('크롤링이 완료되었음.');
      } else {
        await crawlMatches(sport, undefined, date || undefined, crawlerAiProvider || undefined, adminId, notionAdminId);
        toast.success('크롤링이 완료되었음.');
      }
    } catch (error: any) {
      toast.error(error.message || '크롤링에 실패했음.');
    } finally {
      setCrawlerLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.tabContainer}>
        <button
          style={{...styles.tabButton, ...(activeTab === 'prompts' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('prompts')}
        >
          프롬프트
        </button>
        <button
          style={{...styles.tabButton, ...(activeTab === 'scheduler' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('scheduler')}
        >
          스케줄러
        </button>
        <button
          style={{...styles.tabButton, ...(activeTab === 'crawler' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('crawler')}
        >
          크롤러
        </button>
        <button
          style={{...styles.tabButton, ...(activeTab === 'prediction' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('prediction')}
        >
          리포트 관리
        </button>
        <button
          style={{...styles.tabButton, ...(activeTab === 'psynet' ? styles.activeTab : {})}}
          onClick={() => setActiveTab('psynet')}
        >
          Psynet
        </button>
      </div>

      {activeTab === 'prompts' && (
        <PromptManager currentUser={currentUser} />
      )}

      {activeTab === 'scheduler' && (
        <SchedulerManager currentUser={currentUser} />
      )}

      {activeTab === 'crawler' && (
        <div style={styles.form}>
          <CrawlerRequest
            onQuery={handleCrawlerQuery}
            loading={crawlerLoading}
            aiProvider={crawlerAiProvider}
            setAiProvider={setCrawlerAiProvider}
            initialData={initialData}
            isAdministrator={isAdministrator}
            adminList={adminList}
            currentAdminId={currentUser.admin_id}
          />
          <CrawlerHistory adminId={currentUser.admin_id} initialData={initialData} />
        </div>
      )}

      {activeTab === 'prediction' && (
        <PredictionManager currentUser={currentUser} />
      )}

      {activeTab === 'psynet' && (
        <PsynetManager currentUser={currentUser} />
      )}
    </div>
  );
};

const styles = {
  container: commonStyles.container,
  tabContainer: commonStyles.tabContainer,
  tabButton: commonStyles.tabButton,
  activeTab: commonStyles.tabButtonActive,
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: spacing[5],
    paddingBottom: spacing[10],
  },
};

export default ServerConfig;
